/**
 * ProductImages — PDP image carousel with thumbnail strip, zoom, badges, favorites.
 *
 * Matches production SFRA layout:
 * - Main image (single view, no peek)
 * - Badges overlaid top-left
 * - Favorites heart top-right
 * - "Click to Zoom" button bottom-center
 * - Thumbnail strip below with prev/next arrows
 * - Full-screen lightbox on zoom with thumbnail navigation
 * - YouTube video as last slide if c_youtubeID exists
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/imageCarousel.isml
 * - Production screenshots at /tmp/pdp-hsa-tums-top.png
 */
import type React from "react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { EffectFade, Keyboard, Pagination, Zoom } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";

import { ZoomInIcon } from "@radix-ui/react-icons";

import { ProductImage } from "~/components/product-list/product-image";
import { useOptionalVariantContext } from "~/components/products/variant-selector.components";
import { Button } from "~/components/ui/button";
import { Carousel, ZoomControls } from "~/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  type HecProduct,
  getScheduledBadgeTexts,
  isVariantProduct,
} from "~/hec/types/product";
import useImageUtils from "~/hooks/useImageUtils";
import type { C_ProductInfo, Image, ImageGroup } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { useMediaQuery } from "~/lib/utils/screens";
import { getImageGroup, getVariantImage } from "~/shop/utils/image";
import { decodeMasterTilePrice } from "~/shop/utils/scapi-price";
import { generateImageParams } from "~/utils/salesforce-image-transform";

import { FavoritesCTA } from "../../resources/favorites";

export const ProductImagesSkeleton = ({
  compact = false,
}: { compact?: boolean } = {}) => {
  const imageHeight = compact ? "h-[400px]" : "aspect-square";
  return (
    <div
      className="product-images flex w-full flex-col items-start"
      aria-label="Product images loading"
    >
      <Skeleton className={cn("w-full", imageHeight)} />
      {/* Thumbnail strip — matches real component (no top margin) */}
      <div className="flex w-full items-center justify-center gap-2">
        <div className="h-8 w-5 shrink-0" />
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-6 rounded" />
          ))}
        </div>
        <div className="h-8 w-5 shrink-0" />
      </div>
    </div>
  );
};

// Stable Swiper config refs. Swiper React watches `modules` / effect / config
// props by identity; new array/object literals on every render make it tear
// down + re-init, which flickers mid-transition whenever a parent state update
// (e.g. `setActiveSlide` from `onSlideChange`) re-renders during the fade.
// Hoisting to module scope keeps the references stable across re-renders.
const FADE_EFFECT = { crossFade: true };
const ZOOM_KEYBOARD = { enabled: true };
const ZOOM_RATIO = { maxRatio: 3 };
const MAIN_MODULES = [Pagination];
const DESKTOP_ZOOM_MODULES = [Keyboard, EffectFade];
const MOBILE_ZOOM_MODULES = [Keyboard, Zoom, EffectFade];

/**
 * Real-pixel zoom (SFCC-11044, SFRA `detail.js` parity): no CSS transform. The
 * unzoomed modal shows the cached fit image; zooming reveals the actual 3000px
 * hi-res (`getZoomHiResSrc`, server scale-width cap) at NATURAL size and pans
 * it — desktop via `object-position` on mousemove, mobile via native scroll.
 */
const cursorPct = (e: React.MouseEvent<HTMLElement>) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = Math.max(
    0,
    Math.min(100, ((e.clientX - rect.left) / rect.width) * 100),
  );
  const y = Math.max(
    0,
    Math.min(100, ((e.clientY - rect.top) / rect.height) * 100),
  );
  return `${x}% ${y}%`;
};

/**
 * ZoomableImage (desktop >991px): click toggles zoom; the hi-res `<img>` is
 * rendered at natural size (`object-fit: none`) and panned by `object-position`
 * tracking the cursor — true pixels, no transform. The cached fit image stays
 * underneath until the hi-res decodes (no blank flash).
 */
function ZoomableImage({
  image,
  hiResSrc,
}: {
  image: Image;
  hiResSrc: string;
}) {
  const [zoomed, setZoomed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pos, setPos] = useState("50% 50%");

  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      <button
        type="button"
        aria-label={zoomed ? "Zoom out" : "Zoom in"}
        className={cn(
          "flex h-full w-full items-center justify-center bg-transparent p-0",
          zoomed ? "cursor-zoom-out" : "cursor-zoom-in",
        )}
        onClick={() => setZoomed(z => !z)}
        onMouseMove={e => zoomed && setPos(cursorPct(e))}
        onMouseLeave={() => {
          setZoomed(false);
          setPos("50% 50%");
        }}
      >
        <div
          data-testid="zoom-scalable"
          // Fills the full modal container — width minus the side-arrow gutters
          // (slide `px-12/md:px-16`), full viewport height (close is a corner
          // overlay). `h-screen` is a definite height so the absolute inset-0
          // layers resolve a box; overflow-hidden clips the natural-size hi-res
          // so it pans in place.
          className="relative h-screen w-full overflow-hidden"
        >
          {/* Fit view — cached PDP candidate (instant, reused). */}
          <ProductImage
            image={image}
            type="pdp"
            lazy={false}
            className="!absolute !inset-0 h-full w-full select-none object-contain"
          />
          {/* Hi-res real-pixel layer — natural size, panned by object-position;
              fades in once decoded so the fit image covers the load. */}
          {zoomed && (
            <img
              src={hiResSrc}
              alt=""
              draggable={false}
              onLoad={() => setLoaded(true)}
              style={{ objectFit: "none", objectPosition: pos }}
              className={cn(
                "absolute inset-0 h-full w-full select-none transition-opacity duration-150",
                loaded ? "opacity-100" : "opacity-0",
              )}
            />
          )}
        </div>
      </button>
    </div>
  );
}

type ZoomVariantProps = {
  images: Image[];
  initialSlide: number;
  onActiveSlideChange: (index: number) => void;
  getHiRes: (image: Image) => string;
  /** Mobile fit src (single ~1000px url; matches the cached PDP candidate). */
  getFit: (image: Image) => string;
  /**
   * YouTube video id. When present the zoom carousel renders the video as a
   * real playable iframe slide LAST — same order as the main carousel — so the
   * main carousel's activeSlide maps 1:1 onto `initialSlide` and opening zoom
   * from the video slide lands on the video (SFRA `videoIframeZoomComponent`
   * parity: the modal plays the video, it isn't a still image).
   */
  youtubeID?: string;
};

/** Playable YouTube iframe used as the last zoom slide (SFRA `?rel=0` parity). */
function ZoomVideoSlide({ youtubeID }: { youtubeID: string }) {
  // Fill the slide the same way `ZoomableImage` does — a definite `h-screen` box
  // + absolute-fill child. aspect-ratio is unreliable here: an iframe's intrinsic
  // 150px box wins in flow, and on a flex slide aspect-ratio collapses to 0 out
  // of flow. A definite height is the robust anchor; the YouTube player
  // letterboxes its own 16:9 inside, so filling the box doesn't distort it.
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      {/* h-SCREEN (not h-full): the desktop zoom slide has no definite height, so
          h-full collapses the box to 0 (the original thin-band bug). h-screen is
          the definite anchor; overflow-hidden on the parent clips the ~136px it
          over-extends on mobile — only YouTube's letterbox bars, not the video. */}
      <div className="relative h-screen w-full">
        <iframe
          title="Product Video"
          src={`https://www.youtube.com/embed/${youtubeID}?rel=0`}
          allowFullScreen
          frameBorder={0}
          className="absolute inset-0 h-full w-full"
        />
      </div>
    </div>
  );
}

/** Desktop zoom (>991px): contained fit image, side-arrow paging, click real-zoom. */
function DesktopZoom({
  images,
  initialSlide,
  onActiveSlideChange,
  getHiRes,
  youtubeID,
}: ZoomVariantProps) {
  const slides = [
    ...images.map((image, i) => (
      <ZoomableImage
        key={`zoom-image-${i}`}
        image={image}
        hiResSrc={getHiRes(image)}
      />
    )),
    ...(youtubeID
      ? [<ZoomVideoSlide key="zoom-youtube" youtubeID={youtubeID} />]
      : []),
  ];
  return (
    <Carousel
      keyboard={ZOOM_KEYBOARD}
      initialSlide={initialSlide}
      onSlideChange={({ activeIndex }) => onActiveSlideChange(activeIndex)}
      customNavigation={<ZoomControls />}
      slidesPerView={1}
      // Crossfade between slides (matches SFRA productMainModal `fade: true`).
      effect="fade"
      fadeEffect={FADE_EFFECT}
      speed={300}
      className="mx-0 h-full w-full"
      slideClassName="!flex items-center justify-center px-12 md:px-20"
      modules={DESKTOP_ZOOM_MODULES}
    >
      {slides}
    </Carousel>
  );
}

/**
 * Mobile zoom (<=991px): "Pinch to zoom" label + thumbnail strip + pinch-to-zoom
 * via Swiper's Zoom module (SFRA's mobile c-zoom-modal UX). Each slide is ONE
 * stable `<img>` whose `src` swaps fit→3000px on first pinch (`upgraded` set,
 * grow-only). Swapping `src` on the same element keeps the old frame until the
 * new one decodes — no remount, no revert refetch, so no mid-transition flicker.
 * The hi-res loads only on the pinch gesture (SFRA loads hi-res on zoom).
 */
function MobileZoom({
  images,
  initialSlide,
  onActiveSlideChange,
  getHiRes,
  getFit,
  youtubeID,
}: ZoomVariantProps) {
  const [zoomSwiper, setZoomSwiper] = useState<SwiperType | null>(null);
  const [active, setActive] = useState(initialSlide);
  // Slides upgraded to hi-res by a pinch. Grow-only: never revert (reverting
  // would refetch the fit image and remount → flicker).
  const [upgraded, setUpgraded] = useState<Set<number>>(new Set());

  const slides = [
    ...images.map((image, i) => (
      // .swiper-zoom-container is Swiper's pinch/pan target; a single stable
      // <img> whose src upgrades to hi-res on pinch (no remount → no flicker).
      <div key={`mzoom-${i}`} className="swiper-zoom-container h-full w-full">
        <img
          src={upgraded.has(i) ? getHiRes(image) : getFit(image)}
          alt=""
          draggable={false}
          className="h-full w-full select-none object-contain"
        />
      </div>
    )),
    // Playable video slide last (not a zoom-container — video isn't pinch-zoomed).
    ...(youtubeID
      ? [<ZoomVideoSlide key="mzoom-youtube" youtubeID={youtubeID} />]
      : []),
  ];

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-primary">
        <ZoomInIcon className="h-5 w-5" aria-hidden="true" />
        <span>Pinch to zoom</span>
      </div>
      <Carousel
        keyboard={ZOOM_KEYBOARD}
        zoom={ZOOM_RATIO}
        initialSlide={initialSlide}
        onSwiper={setZoomSwiper}
        onSlideChange={({ activeIndex }) => {
          setActive(activeIndex);
          onActiveSlideChange(activeIndex);
          // Reset any pinch so the next slide starts un-zoomed.
          zoomSwiper?.zoom?.out();
        }}
        onZoomChange={(swiper, scale) => {
          if (scale > 1) {
            const i = swiper.activeIndex;
            setUpgraded(prev => (prev.has(i) ? prev : new Set(prev).add(i)));
          }
        }}
        customNavigation={<ZoomControls />}
        slidesPerView={1}
        // Crossfade between slides (matches SFRA's mobile c-zoom-modal).
        effect="fade"
        fadeEffect={FADE_EFFECT}
        speed={300}
        className="mx-0 min-h-0 flex-1"
        slideClassName="!flex items-center justify-center px-12"
        modules={MOBILE_ZOOM_MODULES}
      >
        {slides}
      </Carousel>
      {(images.length > 1 || youtubeID) && (
        <div className="flex justify-center gap-2 overflow-x-auto px-4 py-3">
          {images.map((image, i) => (
            <button
              key={`mthumb-${i}`}
              type="button"
              aria-label={`View image ${i + 1}`}
              onClick={() => {
                zoomSwiper?.zoom?.out();
                zoomSwiper?.slideTo(i);
              }}
              className={cn(
                "h-16 w-16 shrink-0 overflow-hidden rounded border-2 transition-colors",
                active === i ? "border-accent" : "border-transparent",
              )}
            >
              <ProductImage
                image={image}
                type="plp"
                className="h-full w-full object-contain"
              />
            </button>
          ))}
          {/* Video thumb (play icon) → the playable video slide, index === images.length. */}
          {youtubeID && (
            <button
              key="mthumb-youtube"
              type="button"
              aria-label="View product video"
              onClick={() => {
                zoomSwiper?.zoom?.out();
                zoomSwiper?.slideTo(images.length);
              }}
              className={cn(
                "flex h-16 w-16 shrink-0 items-center justify-center rounded border-2 bg-gray-100 transition-colors",
                active === images.length
                  ? "border-accent"
                  : "border-transparent",
              )}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-6 w-6 text-gray-600"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export const ProductImages = ({
  product,
  compact,
  variantSelection,
  staticImage,
}: {
  product: SerializeFrom<C_ProductInfo>;
  /** Compact mode for quick view — smaller images, no lightbox */
  compact?: boolean;
  /** External variant selection — overrides URL params (for quick view) */
  variantSelection?: Record<string, string>;
  /**
   * ET-216: single-image mode for telehealth (GLP-1) PDPs. Renders the hero
   * image alone — no carousel, no thumbnail strip, no zoom/lightbox, and no
   * favorites heart — per the ET-213 element list ("hide the image carousel
   * because there will be only one image"; favorites hidden for MVP, restored
   * later by ET-255). The badge overlay is deliberately NOT gated: it is what
   * draws the Rx badge the same list requires.
   */
  staticImage?: boolean;
}) => {
  // Live variant selection + per-variant data come from the lifted
  // `<VariantSelector>` Provider in product-details.tsx. When not inside a
  // Selector (e.g. quick view, standalone uses), `ctx` is null and the
  // component gracefully falls back to URL params / master imagery.
  const ctx = useOptionalVariantContext();
  const liveSelected = ctx?.selected ?? null;
  const resolvedVariantData = ctx?.resolvedVariantData ?? null;
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [mainSwiper, setMainSwiper] = useState<SwiperType | null>(null);
  const [searchParams] = useSearchParams();
  const { getImageMediaLink } = useImageUtils();
  // Mobile zoom (<=991px) mirrors SFRA's "Pinch to zoom" variant; desktop keeps
  // the click real-zoom variant. Matches SFRA's hidden-sm-down / d-md-none cutoff.
  const isZoomMobile = useMediaQuery("(max-width: 991px)");
  // Single hi-res source for real-pixel zoom — 3000px is the SFCC scale-width
  // cap (sw=3200 → HTTP 400) and "more than enough". Only fetched when a slide
  // is actually zoomed (the <img> mounts then); fit view reuses the cached PDP.
  const getZoomHiResSrc = (image: Image) =>
    getImageMediaLink(image, {
      params: generateImageParams({
        width: 3000,
        quality: 90,
        format: "webp",
        strip: true,
      }),
    });
  // Mobile fit src — single 1000px webp matching the cached PDP mobile@2x
  // candidate (sw=1000, q80) so the un-pinched view is an instant cache hit.
  const getZoomFitSrc = (image: Image) =>
    getImageMediaLink(image, {
      params: generateImageParams({
        width: 1000,
        quality: 80,
        format: "webp",
        strip: true,
      }),
    });

  // Known variation attribute IDs — filter URL params to prevent
  // non-variant params (hybrid, utm_*, gclid, etc.) from affecting image selection
  const variationAttributeIds = useMemo(
    () =>
      new Set(
        product.variationAttributes?.map((a: { id: string }) => a.id) || [],
      ),
    [product.variationAttributes],
  );

  // Find the selected variant's productId by matching variationValues
  // against the current selection — needed to look up per-variant
  // imageGroups in `resolvedVariantData`.
  const findVariantId = (
    selection: Record<string, string>,
  ): string | undefined => {
    if (!Object.keys(selection).length) return undefined;
    const variants = (product as Record<string, unknown>).variants as
      | Array<{
          productId?: string;
          variationValues?: Record<string, string>;
        }>
      | undefined;
    if (!variants?.length) return undefined;
    return variants.find(v => {
      const vv = v.variationValues ?? {};
      return Object.entries(selection).every(([k, val]) => vv[k] === val);
    })?.productId;
  };

  // SFCC-11248 — the orderable variant SKU for the current selection, handed
  // to the favorites heart so favoriting a variant stores a sellable SKU (the
  // master stays the membership key). Same selection priority as imagesLarge;
  // falls back to the master id when no variant is resolved (nothing selected).
  const orderableSku = (() => {
    const selection: Record<string, string> = {};
    if (liveSelected && Object.keys(liveSelected).length) {
      Object.assign(selection, liveSelected);
    } else if (variantSelection) {
      Object.assign(selection, variantSelection);
    } else {
      for (const [key, value] of searchParams.entries()) {
        if (variationAttributeIds.has(key)) selection[key] = value;
      }
    }
    return findVariantId(selection) ?? product.id;
  })();

  // GA4 identity for the favorites heart (SFCC-12175 W6), resolved once so the
  // `item_id` / `item_variant` / `price` triple can't disagree.
  //
  // Two distinct ways a variant is in play, and `orderableSku` only covers one:
  //  - a MASTER url with a resolved selection → `orderableSku` is that variant
  //  - a VARIANT url (`/<variant-sku>.html`) → `data.id` is already the variant
  //    SKU and `master.masterId` is set, while `findVariantId` returns undefined
  //    (a variant read carries no `variants[]`), so `orderableSku === product.id`
  const pdpMasterId = product.master?.masterId ?? product.id;
  // The variant-read test is `type.variant`, NOT `master?.masterId` truthiness:
  // SCAPI returns a self-referential `master.masterId` on the MASTER as well
  // (verified on 42287m — `master.masterId === product.id`), so the old test
  // resolved `pdpVariantSku` to the master's own id on every master PDP. Two
  // consequences, both live on the wire: `add_to_wishlist` reported
  // `item_variant: "42287m"` for a product with no variant selected, and the
  // price lookup below missed — `resolvedVariantData` is keyed by real variant
  // SKUs, so `[masterId]` was always undefined and the event shipped with no
  // `price`, hence no event `value`. SFCC-12189.
  const isVariantUrl = isVariantProduct(product as unknown as HecProduct);
  const pdpVariantSku = isVariantUrl
    ? product.id
    : orderableSku !== product.id
      ? orderableSku
      : null;

  const imagesLarge = useMemo(() => {
    // Priority: live Selector context (PDP variant click) > external
    // variantSelection (quick view) > filtered URL params > product's own
    // variationValues.
    const urlSelection: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      if (variationAttributeIds.has(key)) {
        urlSelection[key] = value;
      }
    }
    const productVariationValues = (product as Record<string, unknown>)
      .variationValues as Record<string, string> | undefined;
    const selection =
      liveSelected && Object.keys(liveSelected).length
        ? liveSelected
        : variantSelection
          ? variantSelection
          : Object.keys(urlSelection).length > 0
            ? urlSelection
            : (productVariationValues ?? {});

    // Per-variant imageGroups from the deferred fan-out — SCAPI master
    // `variants[]` doesn't carry images, so this is the only way to swap
    // imagery on variant select for products where master `imageGroups`
    // don't have per-variant filters.
    if (resolvedVariantData && Object.keys(selection).length) {
      const variantId = findVariantId(selection);
      const variantImageGroups = variantId
        ? resolvedVariantData[variantId]?.imageGroups
        : undefined;
      if (variantImageGroups?.length) {
        const matched = getImageGroup(variantImageGroups, "large", selection);
        if (matched?.images?.length) return matched.images;
        const largeGroup = variantImageGroups.find(
          (g: ImageGroup) => g.viewType === "large",
        );
        if (largeGroup?.images?.length) return largeGroup.images;
      }
    }

    const fromVariant = getVariantImage(product, selection, "large");
    if (fromVariant && fromVariant.length > 0) return fromVariant;

    // Fallback: get ANY large images from native imageGroups
    const nativeGroups = (
      product as unknown as {
        imageGroups?: Array<{
          viewType?: string;
          images?: Array<{ link: string; alt?: string }>;
        }>;
      }
    ).imageGroups;
    if (nativeGroups?.length) {
      const largeGroup = nativeGroups.find(g => g.viewType === "large");
      if (largeGroup?.images?.length) return largeGroup.images;
      // If no "large" viewType, use first group with images
      const anyGroup = nativeGroups.find(g => g.images && g.images.length > 0);
      if (anyGroup?.images?.length) return anyGroup.images;
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    product,
    searchParams,
    variantSelection,
    resolvedVariantData,
    liveSelected,
  ]);

  // Product custom attributes
  const productAny = product as Record<string, unknown>;
  // SFCC-11916: each badge carries its own display window; SFRA drops the
  // attribute at the model layer once the window closes, so gate here too —
  // otherwise expired badges ("Limited Time Offer") never turn off.
  const badges = getScheduledBadgeTexts({
    c_badging: productAny.c_badging as string | undefined,
    c_badgeDisplayFrom: productAny.c_badgeDisplayFrom as string | undefined,
    c_badgeDisplayTo: productAny.c_badgeDisplayTo as string | undefined,
    c_secondBadging: productAny.c_secondBadging as string | undefined,
    c_secondBadgeDisplayFrom: productAny.c_secondBadgeDisplayFrom as
      | string
      | undefined,
    c_secondBadgeDisplayTo: productAny.c_secondBadgeDisplayTo as
      | string
      | undefined,
  });
  // Strip query params from youtubeID — some BM values include ?si=... tracking params
  const rawYoutubeID = productAny.c_youtubeID as string | undefined;
  const youtubeID = rawYoutubeID?.split("?")[0] || undefined;

  // Build slides array explicitly — Carousel needs a clean array with no falsy values.
  // Square box (PDP + quick view alike): image fills the column width so the badge
  // + favorites overlays hug the image corners instead of floating in gray bands
  // (a fixed h-[400px] box in the narrow QV column left object-contain padding).
  const imageHeight = "aspect-square";
  const slides = useMemo(() => {
    const s: React.ReactNode[] = imagesLarge.map((image, i) => (
      <ProductImage
        key={i}
        image={image}
        type="pdp"
        // Only the first (hero) slide is eager; the rest lazy-load.
        lazy={i !== 0}
        className={cn("w-full object-contain", imageHeight)}
      />
    ));
    if (youtubeID) {
      s.push(
        <div
          key="youtube"
          className={cn(
            "flex w-full items-center justify-center bg-black",
            imageHeight,
          )}
        >
          <iframe
            title="Product Video"
            width="100%"
            height="100%"
            // ?rel=0 matches SFRA videoCarousal.isml (no related-video grid at end).
            src={`https://www.youtube.com/embed/${youtubeID}?rel=0`}
            allowFullScreen
            frameBorder={0}
            className="h-full w-full"
          />
        </div>,
      );
    }
    return s;
  }, [imagesLarge, youtubeID, imageHeight]);

  return (
    <div className="product-images flex w-full flex-col items-start">
      {/* Main image area */}
      <div className="relative w-full bg-gray-01">
        {/* Badge overlays */}
        {badges.length > 0 && (
          <div className="absolute left-0 top-2.5 z-10 flex flex-col gap-1">
            {badges.map((badge, i) => (
              <span
                key={`pdp-badge-${i}`}
                className="inline-flex min-h-[23px] items-center bg-accent px-2 py-0.5 text-xs font-medium text-primary-foreground"
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Favorites heart — plain outline heart, no circular border (SFRA parity).
            SFRA renders fa-heart-o at 22px navy; 24px SVG matches visually. */}
        {/* ET-213 hides the heart on treatment PDPs (`staticImage` is the
            telehealth single-image mode); the GA4 wishlist payload below is
            SFCC-12175 W6 / SFCC-12189 and applies wherever it DOES render. */}
        {!staticImage && (
          <FavoritesCTA
            productId={product.id}
            orderableSku={orderableSku}
            className="absolute right-3.5 top-[17px] z-10"
            iconHeight={24}
            iconWidth={24}
            noBorder
            // SFCC-12175 W6.
            //
            // `category` has no source in this component — the PDP resolves its
            // category tree server-side for GA4_productDetail, not here — so it is
            // omitted rather than sent blank. Same for the W2 site-name
            // `item_brand` fallback: this component has no SiteContext reader, and
            // it serves four surfaces (PDP, quick view, hybrid PDP, and the cart's
            // edit-line-item dialog), so adding a `useSite()` here is not worth it
            // for an optional GA4 field. Both are noted on the ticket.
            wishlistGa4={{
              // `master?.masterId ?? id`, matching `buildGa4ProductDetailItems`
              // and every tile surface. NOT bare `product.id`: on a variant URL
              // (`/<variant-sku>.html`) `data.id` IS the variant SKU and
              // `master.masterId` is set, so bare `id` would report the variant as
              // `item_id` while `view_item` on the very same page reported the
              // master — one product, two ids across two events.
              id: pdpMasterId,
              name: product.name,
              brand: product.brand,
              variant: pdpVariantSku,
              // Price must agree with `variant` above, so the two are resolved
              // from the same branch rather than a shared `??` chain. Pairing a
              // variant SKU with the master's price is confidently wrong on a
              // range master, where the master price is the range MIN — favourite
              // the $39.99 variant and GA4 would otherwise receive its SKU
              // alongside 12.50.
              //
              // That mattered even with the variant lookup first: `orderableSku`
              // resolves SYNCHRONOUSLY from the master's `variants[]`, but
              // `resolvedVariantData` is null until the loader's deferred fan-out
              // settles — so on a first paint of `/master.html?color=blue` a click
              // in that window fell through to the master price. Omitting `price`
              // (hence no event `value`) is the correct degradation; a wrong price
              // is not.
              //
              // On the master branch the decoder fallback is load-bearing, not
              // defensive: a range master carries `{type:"range", min, max}` and NO
              // flat `.sales`, so the flat read alone returns undefined and the
              // event would ship with no `price` at all — the exact defect W2 fixes
              // for `view_item`. Same canonical decoder.
              //
              // On a VARIANT URL the landed product IS the variant, so its own
              // flat `c_priceInfo` already holds that variant's price — read it
              // directly rather than waiting on `resolvedVariantData`, which is
              // null until the deferred fan-out settles. SFCC-12189.
              //
              // The master branch also serves quick view, hybrid PDP and the cart
              // edit-line dialog, where `resolvedVariantData` is null even with a
              // variant selected. It prices from the ORDERABLE variants (COM-240):
              // a flat master `sales.value` can be an unbuyable size (28489m on
              // HSA: 58 vs 28 buyable).
              price: pdpVariantSku
                ? isVariantUrl
                  ? product.c_priceInfo?.priceInfo?.sales?.value
                  : (resolvedVariantData?.[pdpVariantSku]?.priceInfo?.priceInfo
                      ?.sales?.value ??
                    product.c_priceInfo?.priceInfo?.sales?.value)
                : decodeMasterTilePrice(product).decoded?.salesprice,
            }}
          />
        )}

        {/* Main carousel — single image view. `staticImage` renders the hero
            slide on its own: no Swiper instance, so nothing to page through. */}
        {staticImage ? (
          // `slides[0]` is the video when a product has `c_youtubeID` but no
          // large images — a static "hero" must never be an autoloading iframe.
          <div className={cn("mx-0 w-full", imageHeight)}>
            {imagesLarge.length ? slides[0] : null}
          </div>
        ) : (
          <Carousel
            spaceBetween={0}
            className={cn("mx-0 w-full", imageHeight)}
            modules={MAIN_MODULES}
            slidesPerView={1}
            onSwiper={s => setMainSwiper(s)}
            onSlideChange={({ activeIndex }) => setActiveSlide(activeIndex)}
            observer
            observeParents
          >
            {slides}
          </Carousel>
        )}

        {/* Click to Zoom — magnifier + label. Bottom-right on mobile, centered
            at md+ (matches SFRA `&__zoom`: bottom:16/right:30, then centered
            bottom:20 above the md breakpoint). */}
        {!compact && !staticImage && (
          <Button
            variant="ghost"
            aria-label="Click to Zoom"
            className="min-[768px]:bottom-5 min-[768px]:left-1/2 min-[768px]:right-auto min-[768px]:-translate-x-1/2 absolute bottom-4 right-[30px] z-10 flex h-auto w-auto items-center gap-1.5 bg-transparent p-1 font-grotesque text-sm font-light text-foreground hover:bg-transparent"
            onClick={() => setIsLightboxOpen(true)}
          >
            <ZoomInIcon className="h-5 w-5" />
            <span className="min-[768px]:inline hidden">Click to Zoom</span>
          </Button>
        )}
      </div>

      {/* Thumbnail strip with prev/next arrows */}
      {!staticImage && (imagesLarge.length > 1 || youtubeID) && (
        <div className="flex w-full items-center justify-center gap-2">
          {/* Prev arrow */}
          <button
            type="button"
            onClick={() => mainSwiper?.slidePrev()}
            className="flex h-8 w-5 shrink-0 items-center justify-center text-[#142745] hover:text-[#142745]/70"
            aria-label="Previous image"
          >
            {/* Filled chevron matching SFRA's slick arrow (issvg, fill #142745).
                The glyph points down at rest; slick rotates it +90° for the
                prev (left) direction. */}
            <svg
              viewBox="0 0 12 8"
              width="13"
              height="9"
              fill="currentColor"
              className="rotate-90"
            >
              <path
                fillRule="evenodd"
                d="M.351.351c.47-.468 1.229-.468 1.698 0l3.813 3.814L9.677.351c.469-.468 1.228-.468 1.697 0 .469.47.469 1.229 0 1.698L6 7.423l-.138-.138-.137.138L.351 2.049C-.117 1.579-.117.82.351.35z"
              />
            </svg>
          </button>

          {/* Thumbnail buttons — scrollable, auto-scrolls to active */}
          <div className="scrollbar-none flex gap-1 overflow-x-auto">
            {imagesLarge.map((image, i) => (
              <button
                key={`thumb-${i}`}
                type="button"
                ref={el => {
                  if (el && activeSlide === i) {
                    // Scroll only the thumbnail strip horizontally, NOT the page.
                    // scrollIntoView with block:"nearest" still scrolls the page
                    // when React re-renders (e.g., after a fetcher action completes).
                    el.parentElement?.scrollTo({
                      left:
                        el.offsetLeft -
                        el.parentElement.offsetWidth / 2 +
                        el.offsetWidth / 2,
                      behavior: "smooth",
                    });
                  }
                }}
                onClick={() => mainSwiper?.slideTo(i)}
                className={cn(
                  "h-6 w-6 shrink-0 cursor-pointer overflow-hidden rounded border-2 transition-all lg:h-10 lg:w-10",
                  activeSlide === i ? "border-accent" : "border-transparent",
                )}
              >
                <ProductImage
                  image={image}
                  type="plp"
                  className="h-full w-full object-contain"
                />
              </button>
            ))}
            {youtubeID && (
              <button
                type="button"
                aria-label="View product video"
                onClick={() => mainSwiper?.slideTo(imagesLarge.length)}
                className={cn(
                  "flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded border-2 bg-gray-100 transition-all lg:h-10 lg:w-10",
                  activeSlide === imagesLarge.length
                    ? "border-accent"
                    : "border-transparent",
                )}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-5 w-5 text-gray-600"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
          </div>

          {/* Next arrow */}
          <button
            type="button"
            onClick={() => mainSwiper?.slideNext()}
            className="flex h-8 w-5 shrink-0 items-center justify-center text-[#142745] hover:text-[#142745]/70"
            aria-label="Next image"
          >
            {/* Filled chevron matching SFRA's slick arrow (issvg, fill #142745).
                The glyph points down at rest; slick rotates it -90° for the
                next (right) direction. */}
            <svg
              viewBox="0 0 12 8"
              width="13"
              height="9"
              fill="currentColor"
              className="-rotate-90"
            >
              <path
                fillRule="evenodd"
                d="M.351.351c.47-.468 1.229-.468 1.698 0l3.813 3.814L9.677.351c.469-.468 1.228-.468 1.697 0 .469.47.469 1.229 0 1.698L6 7.423l-.138-.138-.137.138L.351 2.049C-.117 1.579-.117.82.351.35z"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Lightbox / Zoom dialog */}
      {!compact && !staticImage && (
        <Dialog
          open={isLightboxOpen}
          onOpenChange={() => setIsLightboxOpen(false)}
        >
          <DialogContent
            // !animate-none disables the default Dialog slide/zoom/fade so the
            // zoom modal just appears (SFRA shows no open animation).
            className="fixed left-0 top-0 block h-full w-full max-w-none translate-x-0 translate-y-0 bg-page-background p-0 outline-none data-[state=closed]:!animate-none data-[state=open]:!animate-none [&>button]:z-10"
            aria-describedby={undefined}
            // Don't pull focus onto a nav arrow on open (SFRA shows no focused
            // control); the dialog stays labeled + Escape-closable + focus-trapped.
            onOpenAutoFocus={e => e.preventDefault()}
          >
            <DialogTitle className="sr-only">Product Image Zoom</DialogTitle>
            {/* Parity with SFRA c-zoom-modal, responsive real-pixel zoom (no
                transform):
                - Desktop (>991px): single contained fit image, side-arrow
                  paging; click reveals the 3000px image at natural size, panned
                  by object-position on mousemove.
                - Mobile (<=991px): "Pinch to zoom" label + thumbnail strip; tap
                  scroll-pans the natural-size 3000px image.
                (SFCC-11044 / PDP-MEDIA-01) */}
            <div className="h-full w-full" data-testid="pdp-zoom-modal">
              {isZoomMobile ? (
                <MobileZoom
                  images={imagesLarge}
                  initialSlide={activeSlide}
                  onActiveSlideChange={setActiveSlide}
                  getHiRes={getZoomHiResSrc}
                  getFit={getZoomFitSrc}
                  youtubeID={youtubeID}
                />
              ) : (
                <DesktopZoom
                  images={imagesLarge}
                  initialSlide={activeSlide}
                  onActiveSlideChange={setActiveSlide}
                  getHiRes={getZoomHiResSrc}
                  getFit={getZoomFitSrc}
                  youtubeID={youtubeID}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
